import styled from 'styled-components';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import {
  IReportSettingEditMutation,
  IReportSettingEditMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { EDIT_REPORT_SETTING } from '../../../../utils/apollo/globalConfig';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../../../utils/toastMessage';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import { assertNotNil } from '../../../../utils/types';
import { IReportSettingsFormState, reportSettingsSchema } from './types';
import { useAppDispatch } from '../../../../redux/hooks';
import { updateReportSetting } from '../../../../redux/slices/bootstrap';
import ThinDivider from '../../../../components/ThinDivider';
import { prepareReportSettingEditInput } from './utils';
import FunnelReportSettings from './FunnelReportSettings';
import CohortInspectReportSettings from './CohortInspectReportSettings';
import { theme } from '../../../../styles/theme';

const Container = styled.form`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  margin-top: 24px;
`;
const SubTitle = styled.div`
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary800};
`;
const ActionsWrapper = styled.div`
  display: flex;
  column-gap: 16px;
  margin-top: 8px;
`;

const ReportSettingsForm = () => {
  const dispatch = useAppDispatch();
  const currentReportSetting = useBootstrapSelector('reportSetting');
  assertNotNil(currentReportSetting);
  const { funnel, inspect } = currentReportSetting;
  const { defaultConversionRate } = funnel;
  const { schemaInspectSettings } = inspect;

  const [updateReportSettings] = useMutation<
    IReportSettingEditMutation,
    IReportSettingEditMutationVariables
  >(EDIT_REPORT_SETTING);

  const formMethods = useForm<IReportSettingsFormState>({
    resolver: zodResolver(reportSettingsSchema),
    defaultValues: {
      funnel: {
        defaultConversionRate,
      },
      inspect: {
        schemaInspectSettings: schemaInspectSettings.map((inspectSetting) => ({
          schemaKey: inspectSetting.schema.id,
          fields: inspectSetting.fields,
        })),
      },
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
  } = formMethods;

  const onSubmitForm = handleSubmit(async (formData) => {
    const response = await updateReportSettings({
      variables: {
        input: prepareReportSettingEditInput(formData),
      },
    });

    if (printGraphqlErrors(response?.data?.reportSettingEdit?.errors)) {
      return;
    }

    const entity = response?.data?.reportSettingEdit?.entity;
    assertNotNil(entity);
    dispatch(updateReportSetting(entity));

    toastMessage({ message: 'Chart Settings were updated.', type: 'info' });
    reset(formData);
  });

  const handleReset = () => {
    reset();
  };

  return (
    <FormProvider {...formMethods}>
      <Container onSubmit={onSubmitForm}>
        <div>
          <SubTitle>Funnel</SubTitle>
          <FunnelReportSettings />
        </div>
        <ThinDivider layout="horizontal" size="full" />
        <div>
          <SubTitle>Cohort Inspect</SubTitle>
          <CohortInspectReportSettings />
        </div>
        <ActionsWrapper>
          <PrimaryButton
            type="submit"
            disabled={isSubmitting || !isDirty}
            trackContext="report settings"
            trackAction="save"
          >
            Save
          </PrimaryButton>
          <SecondaryButton
            type="button"
            onClick={handleReset}
            disabled={isSubmitting || !isDirty}
            isOutlined={true}
            trackContext="report settings"
            trackAction="reset"
          >
            Reset
          </SecondaryButton>
        </ActionsWrapper>
      </Container>
    </FormProvider>
  );
};

export default ReportSettingsForm;
